import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { getTagColor, blendColors } from '../utils/tagColors';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.name === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.6)'};
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.name === 'dark' 
    ? 'linear-gradient(135deg, rgba(26, 26, 46, 0.95) 0%, rgba(22, 33, 62, 0.95) 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 249, 250, 0.98) 100%)'
  };
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 20px;
  max-width: 800px;
  max-height: 90vh;
  width: 100%;
  min-width: 320px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 20px 40px ${props => props.theme.colors.shadow};
  animation: ${slideIn} 0.3s ease-out;
  display: flex;
  flex-direction: column;
`;

const HeaderContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 120px;
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: 20px 20px 0 0;
  background: ${props => props.tagBackground || (props.theme.name === 'dark' 
    ? 'linear-gradient(45deg, #1a1a2e, #16213e)'
    : 'linear-gradient(45deg, #f8f9fa, #e9ecef)')
  };
  transition: background 0.3s ease;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  font-weight: bold;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.borderLight};
    transform: scale(1.1);
    box-shadow: 0 0 15px ${props => props.theme.colors.shadow};
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 400px) {
    width: 36px;
    height: 36px;
    min-width: 36px;
    font-size: 1rem;
    top: 0.75rem;
    right: 0.75rem;
  }
`;

// PredictionImage styled component removed - no longer needed since we're showing gradient background only

const ScrollableContent = styled.div`
  overflow-y: auto;
  flex: 1;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.borderLight};
    border-radius: 4px;
    margin: 20px 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.scrollbarThumb};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.scrollbarThumbHover};
  }
`;

const ContentArea = styled.div`
  padding: 2rem;
`;

const PredictionYear = styled.div`
  display: inline-block;
  background: ${props => props.theme.name === 'dark' 
    ? 'linear-gradient(45deg, #64ffda, #bb86fc)'
    : 'linear-gradient(45deg, #3b82f6, #8b5cf6)'
  };
  color: #ffffff;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  box-shadow: 0 4px 15px ${props => props.theme.colors.shadow};
`;

const PredictionTitle = styled.h2`
  color: ${props => props.theme.colors.text};
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 1.5rem 0;
  line-height: 1.3;
  background: ${props => props.theme.name === 'dark' 
    ? 'linear-gradient(45deg, #64ffda, #bb86fc, #64ffda)'
    : 'linear-gradient(45deg, #3b82f6, #8b5cf6, #3b82f6)'
  };
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  background-size: 200% 100%;
  animation: shimmer 3s ease-in-out infinite;
  
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const PredictionText = styled.p`
  color: ${props => props.theme.colors.text};
  font-size: 1.1rem;
  line-height: 1.7;
  margin: 0 0 2rem 0;
  text-align: justify;
`;

const SourceText = styled.div`
  background: ${props => props.theme.name === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(0, 0, 0, 0.05)'
  };
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  padding: 1rem;
  margin: 0 0 1.5rem 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  line-height: 1.5;
  color: ${props => props.theme.name === 'dark' 
    ? 'rgba(255, 255, 255, 0.8)'
    : 'rgba(0, 0, 0, 0.7)'
  };
  position: relative;
  
  &::before {
    content: 'Source Text (Debug)';
    position: absolute;
    top: -0.5rem;
    left: 1rem;
    background: ${props => props.theme.colors.surface};
    padding: 0 0.5rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: ${props => props.theme.name === 'dark' 
      ? 'rgba(255, 255, 255, 0.6)'
      : 'rgba(0, 0, 0, 0.6)'
    };
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  @media (max-width: 600px) {
    font-size: 0.8rem;
    padding: 0.75rem;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 2rem 0;
`;

const Tag = styled.span`
  background: ${props => props.tagColor || props.theme.colors.borderLight};
  border: 2px solid ${props => props.tagColor ? props.tagColor.replace('0.3)', '0.6)').replace('0.5)', '0.8)') : props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  padding: 0.4rem 0.8rem;
  border-radius: 15px;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px ${props => props.theme.colors.shadow};
  }
`;

const MetadataContainer = styled.div`
  background: ${props => props.theme.colors.surfaceSecondary};
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 2rem;
  border: 1px solid ${props => props.theme.colors.borderLight};
`;

const MetadataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const MetadataLabel = styled.span`
  color: ${props => props.theme.name === 'dark' ? '#64ffda' : '#3b82f6'};
  font-weight: 600;
  font-size: 0.9rem;
`;

const MetadataValue = styled.span`
  color: ${props => props.theme.colors.text};
  font-size: 0.9rem;
  max-width: 60%;
  text-align: right;
  
  @media (max-width: 600px) {
    max-width: 100%;
    text-align: left;
  }
`;

const LinkButton = styled.a`
  color: ${props => props.theme.name === 'dark' ? '#64ffda' : '#3b82f6'};
  text-decoration: none;
  padding: 0.3rem 0.8rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.borderLight};
    transform: translateY(-1px);
  }
`;

const PredictionModal = ({ prediction, onClose, showDebugInfo = false }) => {
  const { themeData } = useTheme();
  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!prediction) return null;

  // Parse tags from array format or fallback to pipe/comma separated
  const tags = (() => {
    if (!prediction.tags) return [];
    if (typeof prediction.tags !== 'string') return [];
    
    try {
      // Handle array format like "['tag1', 'tag2']"
      return JSON.parse(prediction.tags.replace(/'/g, '"')).filter(tag => tag && tag.length > 0);
    } catch {
      // Fallback: treat as string and split by pipe or comma
      return prediction.tags.split(/[|,]/).map(tag => tag.trim()).filter(tag => tag.length > 0);
    }
  })();

  // Generate dynamic background color based on prediction tags - matches PredictionBubble
  const tagBackground = blendColors(prediction.tags, '0.8'); // Higher alpha for more vibrant background

  return (
    <ModalOverlay onClick={handleOverlayClick} theme={themeData}>
      <ModalContent theme={themeData}>
        <HeaderContainer theme={themeData} tagBackground={tagBackground}>
          <CloseButton onClick={onClose} theme={themeData}>×</CloseButton>
        </HeaderContainer>
        
        <ScrollableContent theme={themeData}>
          <ContentArea>
            <PredictionYear theme={themeData}>
              {prediction.timeframe || prediction.display_year}
            </PredictionYear>
            
            <PredictionTitle theme={themeData}>{prediction.title}</PredictionTitle>
            
            <PredictionText theme={themeData}>{prediction.text}</PredictionText>
          
          {/* Only show source text in private dataset mode */}
          {showDebugInfo && prediction.raw_text && prediction.raw_text.trim() && (
            <SourceText theme={themeData}>
              {prediction.raw_text}
            </SourceText>
          )}
          
          {tags.length > 0 && (
            <TagsContainer>
              {tags.map((tag, index) => (
                <Tag key={index} tagColor={getTagColor(tag, themeData.colors.bubbleAlpha)} theme={themeData}>{tag}</Tag>
              ))}
            </TagsContainer>
          )}
          
          {/* Prediction Information Section */}
          <MetadataContainer theme={themeData}>
            {prediction.author && (
              <MetadataRow>
                <MetadataLabel theme={themeData}>Predicted by:</MetadataLabel>
                <MetadataValue theme={themeData}>
                  {(() => {
                    try {
                      // Handle array format like "['Nick Bostrom']"
                      const authorList = JSON.parse(prediction.author.replace(/'/g, '"'));
                      return authorList.join(', ');
                    } catch {
                      // Fallback: treat as single author string
                      return prediction.author;
                    }
                  })()}
                </MetadataValue>
              </MetadataRow>
            )}
            
            {prediction.date && (
              <MetadataRow>
                <MetadataLabel theme={themeData}>Predicted In:</MetadataLabel>
                <MetadataValue theme={themeData}>{prediction.date}</MetadataValue>
              </MetadataRow>
            )}
            
            {prediction.timeframe && (
              <MetadataRow>
                <MetadataLabel theme={themeData}>Predicted for:</MetadataLabel>
                <MetadataValue theme={themeData}>{prediction.timeframe}</MetadataValue>
              </MetadataRow>
            )}
            
            <MetadataRow>
              <MetadataLabel theme={themeData}>Display year:</MetadataLabel>
              <MetadataValue theme={themeData}>{prediction.display_year}</MetadataValue>
            </MetadataRow>
            
            <MetadataRow>
              <MetadataLabel theme={themeData}>Prediction Came True:</MetadataLabel>
              <MetadataValue theme={themeData}>
                {(() => {
                  const realized = prediction.realized;
                  if (realized === 'Yes' || realized === 'TRUE') return 'Yes';
                  if (realized === 'No' || realized === 'FALSE') return 'No';
                  return 'Not yet';
                })()}
              </MetadataValue>
            </MetadataRow>
            
            {(prediction.realized === 'Yes' || prediction.realized === 'TRUE') && prediction.realized_date && (
              <MetadataRow>
                <MetadataLabel theme={themeData}>Came true in:</MetadataLabel>
                <MetadataValue theme={themeData}>{prediction.realized_date}</MetadataValue>
              </MetadataRow>
            )}
            
            {/* Evidence Section */}
            {(() => {
              // Only show evidence if prediction came true AND there are actual links (URLs)
              const hasRealized = prediction.realized === 'Yes' || prediction.realized === 'TRUE';
              const hasLinks = prediction.realized_links && prediction.realized_links.trim();
              
              if (!hasRealized || !hasLinks) return null;
              
              // Check if realized_links contains actual URLs
              const links = prediction.realized_links.split(',').map(link => link.trim()).filter(link => {
                return link.startsWith('http://') || link.startsWith('https://');
              });
              
              // Only show if there are actual URLs
              if (links.length === 0) return null;
              
              return (
                <MetadataRow>
                  <MetadataLabel theme={themeData}>Evidence:</MetadataLabel>
                  <MetadataValue theme={themeData}>
                    {links.map((link, index) => (
                      <LinkButton 
                        key={index}
                        href={link} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        theme={themeData}
                        style={{ marginRight: '0.5rem' }}
                      >
                        {index + 1}
                      </LinkButton>
                    ))}
                  </MetadataValue>
                </MetadataRow>
              );
            })()}
          </MetadataContainer>

          {/* Source Information Section */}
          <MetadataContainer theme={themeData} style={{ marginTop: '1rem' }}>
            {prediction.source && (
              <MetadataRow>
                <MetadataLabel theme={themeData}>Source:</MetadataLabel>
                <MetadataValue theme={themeData}>{prediction.source}</MetadataValue>
              </MetadataRow>
            )}
            
            {prediction.link && prediction.link.trim() && (
              <MetadataRow>
                <MetadataLabel theme={themeData}>Link to Source:</MetadataLabel>
                <MetadataValue theme={themeData}>
                  <LinkButton href={prediction.link} target="_blank" rel="noopener noreferrer" theme={themeData}>
                    View on Amazon
                  </LinkButton>
                </MetadataValue>
              </MetadataRow>
            )}
          </MetadataContainer>
          </ContentArea>
        </ScrollableContent>
      </ModalContent>
    </ModalOverlay>
  );
};

PredictionModal.propTypes = {
  prediction: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    text: PropTypes.string,
    tags: PropTypes.string,
    author: PropTypes.string,
    display_year: PropTypes.number,
    timeframe: PropTypes.string,
    date: PropTypes.string,
    source: PropTypes.string,
    link: PropTypes.string,
    realized: PropTypes.string,
    realized_date: PropTypes.string,
    realized_links: PropTypes.string,
    raw_text: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  showDebugInfo: PropTypes.bool
};

PredictionModal.defaultProps = {
  showDebugInfo: false
};

export default PredictionModal; 